import { useEffect, useState } from 'react';
import { Button, Dialog, Input, Select, Alert } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';

export const KIND_LABELS = {
  http: { label: 'HTTP API', hint: "calls the customer's REST API" },
  mcp: { label: 'MCP server', hint: 'tools discovered from a remote MCP server' },
  a2a: { label: 'A2A agent', hint: 'delegate to a remote agent via its Agent Card (Agent2Agent)' },
  client: { label: 'Browser page', hint: 'handlers in the page, via the embed widget' },
  platform: { label: 'Platform (first-party)', hint: 'the platform\'s own privileged tools, bound in-process — seeded, not creatable here' },
};

function FieldHint({ children }) {
  return <p className="text-xs text-secondary-500 mt-1">{children}</p>;
}

const BLANK = { kind: 'http', slug: '', name: '', baseUrl: '', embedKeyId: '', authConnectionId: '', application_id: null };

/**
 * Choose the outbound credential at CREATE time.
 *
 * It matters that this is here and not only on the provider page: creating an MCP provider
 * fires an immediate discovery sync, so a server that needs authentication fails it and the
 * provider lands in an error state the user has to clear by hand.
 */
function McpCredentialPicker({ value, connections, onChange }) {
  return (
    <div>
      <Select
        label="Credential for this server (optional)"
        value={value}
        onChange={onChange}
        allowClear
        searchable
        options={[
          { value: '', label: 'None — public server, or use this provider\u2019s signing secret' },
          ...connections.map((c) => ({ value: String(c.id), label: c.name })),
        ]}
      />
      <FieldHint>
        A server that signs you in with OAuth needs nothing here — create the provider, then
        use Connect on its page. Other credentials are created on the Connections screen.
      </FieldHint>
    </div>
  );
}

/**
 * Shared create dialog for tool providers — used on the Tool Providers list and
 * inline from AgentDetail's MCP Servers card (like adding a KB from the agent).
 * The server mints the signing secret and shows it exactly once.
 */
export default function ToolProviderCreateDialog({ isOpen, onClose, onCreated, kindPreset, defaultBaseUrl }) {
  const { fetchApi } = useAppApi();
  const [form, setForm] = useState(BLANK);
  const [embedKeys, setEmbedKeys] = useState([]);
  const [mcpConnections, setMcpConnections] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  const [secret, setSecret] = useState(null);
  const [createdId, setCreatedId] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!isOpen) return;
    const initialKind = kindPreset && KIND_LABELS[kindPreset] ? kindPreset : 'http';
    setForm({ ...BLANK, kind: initialKind, baseUrl: defaultBaseUrl || '' });
    setSecret(null);
    setCreatedId(null);
    setErr('');
    setApplicationId(null);
    fetchApi('crud', { model: 'AppEmbedKey', action: 'list', limit: 100 })
      .then((res) => setEmbedKeys((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setEmbedKeys([]));
    fetchApi('crud', { model: 'AppConnection', action: 'list', where: { category_key: 'mcp' }, limit: 200 })
      .then((res) => setMcpConnections((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setMcpConnections([]));
    // A remote A2A agent authenticates with whatever its owner issued (a bearer token, an
    // API key), so any connection may hold it — there is no A2A connection category.
    fetchApi('crud', { model: 'AppConnection', action: 'list', limit: 200 })
      .then((res) => setAllConnections((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setAllConnections([]));
    fetchApi('applicationsList').then((res) => setApps(Array.isArray(res) ? res : [])).catch(() => {});
  }, [isOpen, kindPreset, defaultBaseUrl]);

  const submit = async () => {
    setSaving(true);
    setErr('');
    try {
      const res = await fetchApi('toolProviderCreate', {
        ...form,
        embedKeyId: form.kind === 'client' ? Number(form.embedKeyId) || null : null,
        baseUrl: form.kind === 'client' ? null : form.baseUrl,
        // Bound BEFORE the auto-sync below, not after. A server that needs a credential
        // 401s on the create-time discovery otherwise, and the provider lands in an error
        // state the user then has to clear by hand.
        authConnectionId: form.authConnectionId || null,
        application_id: applicationId || null,
      });
      setSecret(res.secret);
      setCreatedId(res.id || null);
      // Best-effort auto-sync for MCP so the agent can attach tools immediately
      if ((form.kind === 'mcp' || form.kind === 'a2a') && res.id) {
        fetchApi('toolProviderSync', res.id).catch(() => {});
      }
      if (typeof onCreated === 'function') onCreated({ id: res.id, secret: res.secret, kind: form.kind });
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // if a secret was shown, the caller likely wants to act on the new id now
    const payload = createdId ? { id: createdId, secret, kind: form.kind } : null;
    setSecret(null);
    setCreatedId(null);
    onClose(payload);
  };

  if (secret) {
    return (
      <Dialog isOpen={isOpen} onClose={handleClose} title="Signing secret — copy it now" size="md">
        <div className="space-y-3">
          <Alert variant="warning" title="Shown only once">
            This secret signs our calls into your API and verifies the tool manifest your
            page presents. Store it wherever your app keeps its credentials — it cannot be
            retrieved later, only replaced.
          </Alert>
          <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs break-all whitespace-pre-wrap">{secret}</pre>
          <p className="text-xs text-secondary-500">
            {form.kind === 'mcp'
              ? 'For a public MCP server the secret may be left unused (leave endpoint unauthenticated). For a private one, configure your MCP server to accept Authorization: Bearer <secret>.'
              : null}
          </p>
          <Button onClick={handleClose}>I&apos;ve stored it</Button>
        </div>
      </Dialog>
    );
  }

  const kindOptions = Object.entries(KIND_LABELS).map(([value, k]) => ({ value, label: `${k.label} — ${k.hint}` }));
  const isPreset = !!kindPreset && !!KIND_LABELS[kindPreset];

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} title={isPreset && kindPreset === 'mcp' ? 'New MCP server' : 'New tool provider'} size="md">
      <div className="space-y-3">
        {err && <Alert variant="error">{err}</Alert>}
        <Select
          label="Where do these tools run?"
          value={form.kind}
          onChange={(v) => set({ kind: v })}
          options={kindOptions}
          disabled={isPreset}
        />
        {isPreset && <FieldHint>Kind is fixed for this shortcut — create other kinds from the Tool Providers page.</FieldHint>}
        <Input label="Name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder={form.kind === 'mcp' ? 'Procurely' : 'Acme Storefront API'} />
        <div>
          <Input label="Slug" value={form.slug} onChange={(e) => set({ slug: e.target.value })} placeholder={form.kind === 'mcp' ? 'procurely' : 'acme'} />
          <FieldHint>
            Prefixes every tool name the model sees (<code>{form.slug || 'acme'}_add_item</code>), so it can
            never collide with a built-in. Lower-case letters, digits and underscores.
          </FieldHint>
        </div>
        {form.kind !== 'client' && (
          <Input
            label={{ mcp: 'MCP endpoint URL', a2a: 'Agent Card URL (or the agent\u2019s base URL)' }[form.kind] || 'Base URL'}
            value={form.baseUrl}
            onChange={(e) => set({ baseUrl: e.target.value })}
            placeholder={{ mcp: 'https://procurely.stage.drm3.org/api/mcp', a2a: 'https://agents.example.com/.well-known/agent-card.json' }[form.kind] || 'https://api.example.com/agent'}
          />
        )}
        {form.kind === 'mcp' && (
          <McpCredentialPicker value={form.authConnectionId} connections={mcpConnections} onChange={(v) => set({ authConnectionId: v })} />
        )}
        {form.kind === 'mcp' && (
          <FieldHint>
            Must be the full Streamable HTTP endpoint (e.g. <code>/api/mcp</code>). The platform will call{' '}
            <code>listTools</code> at this URL to discover tools — tools are pinned and require
            approval before the agent can call them.
          </FieldHint>
        )}
        {form.kind === 'a2a' && (
          <McpCredentialPicker value={form.authConnectionId} connections={allConnections} onChange={(v) => set({ authConnectionId: v })} />
        )}
        {form.kind === 'a2a' && (
          <FieldHint>
            The platform fetches the Agent Card, pins it, and creates two tools:{' '}
            <code>{form.slug || 'slug'}_ask</code> (delegate a message, gated and marked destructive until
            you review it) and <code>{form.slug || 'slug'}_get_task</code> (check a long-running task).
            A changed card unbinds the agent until you re-sync and re-approve.
          </FieldHint>
        )}
        {form.kind === 'client' && (
          <div>
            <Select
              label="Embed key"
              value={form.embedKeyId}
              onChange={(v) => set({ embedKeyId: v })}
              options={[{ value: '', label: 'Select a key…' },
                ...embedKeys.map((k) => ({ value: String(k.id), label: k.label || k.key }))]}
            />
            <FieldHint>Only a manifest signed with this key&apos;s identity secret may declare these tools.</FieldHint>
          </div>
        )}
        <Select
          label="Application"
          options={[{ label: 'Company-wide (all apps)', value: '' }, ...apps.map(a => ({ label: a.name, value: a.id }))]}
          value={applicationId || ''}
          onChange={(v) => setApplicationId(v || null)}
          allowClear
          searchable
        />
        <div className="flex gap-2">
          <Button onClick={submit} disabled={saving}>Create</Button>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
        </div>
      </div>
    </Dialog>
  );
}
